import type { ReactNode } from "react";

function H2({ children }: { children: ReactNode }) {
	return (
		<h2
			className="mt-10 font-mono text-[clamp(1.15rem,2.2vw,1.4rem)] leading-[1.2] tracking-[-0.01em] text-foreground"
			style={{ fontWeight: 400 }}
		>
			{children}
		</h2>
	);
}

function H3({ children }: { children: ReactNode }) {
	return (
		<h3 className="mt-7 font-mono text-base leading-[1.3] text-foreground">
			{children}
		</h3>
	);
}

function P({ children }: { children: ReactNode }) {
	return (
		<p className="mt-4 text-base leading-[1.65] text-muted-foreground">
			{children}
		</p>
	);
}

function A({ href, children }: { href: string; children: ReactNode }) {
	const external = href.startsWith("http");

	return (
		<a
			href={href}
			target={external ? "_blank" : undefined}
			rel={external ? "noopener noreferrer" : undefined}
			className="text-foreground underline underline-offset-4 transition-colors hover:text-brand"
		>
			{children}
		</a>
	);
}

function Note({ children }: { children: ReactNode }) {
	return (
		<div className="mt-6 border border-border bg-card p-4">{children}</div>
	);
}

function Screenshot({
	src,
	alt,
	caption,
}: {
	src: string;
	alt: string;
	caption: string;
}) {
	return (
		<figure className="mt-6">
			<img
				src={src}
				alt={alt}
				width={1280}
				height={720}
				loading="lazy"
				className="block h-auto w-full border border-border"
			/>
			<figcaption className="mt-2 font-mono text-xs leading-[1.5] text-muted-foreground">
				{caption}
			</figcaption>
		</figure>
	);
}

export function McpServerSecurityChecklistPost() {
	return (
		<div>
			<P>
				Treat every MCP server as code plus an account connection. Check who
				publishes it, what command runs, which data it can reach, and what
				actions it can take before you connect it.
			</P>
			<P>
				Start with read-only access and test with data you can replace. Give the
				server broader access only when the task needs it and the first test
				behaves as expected.
			</P>

			<H2>&gt; The MCP server security checklist</H2>
			<Note>
				<p className="text-base leading-[1.6] text-muted-foreground">
					An MCP server gives an AI application a standard way to use tools,
					read resources, and load prompts. That access can be useful, but the
					risk depends on the server. A file tool may read or change local
					files. A sales tool may open customer records. A browser tool may act
					inside a signed-in session.
				</p>
				<p className="mt-4 text-base leading-[1.6] text-muted-foreground">
					Review the server as both software and an account integration. Define
					the task first, find a small set of candidates, verify the publisher
					and exact install command, inspect the exposed tools, limit
					credentials, test away from live data, and keep a way to revoke
					access. The official{" "}
					<A href="https://modelcontextprotocol.io/docs/2026-07-28/tutorials/security/security_best_practices">
						MCP security guidance
					</A>{" "}
					recommends clear consent before a local command runs and a minimum
					initial permission scope. A directory listing helps you discover a
					server. It doesn't replace this review. The safe choice is the server
					that completes the job with the least access, not the one with the
					longest tool list.
				</p>
			</Note>

			<H3>1. Write down the task before choosing a server</H3>
			<P>
				Name the one job the server needs to do. “Read support tickets” is
				useful. “Access our support system” is too broad. The sharper task tells
				you which data, actions, and account rights are necessary.
			</P>
			<P>
				This boundary lets you reject any server that asks for access unrelated
				to the job. It also keeps the test small enough to inspect.
			</P>

			<H3>2. Find candidates without treating a listing as approval</H3>
			<P>
				Use <A href="https://allmcps.com">AllMCPs</A> to search and browse MCP
				servers by the job your agent needs to do. Build a shortlist there, then
				open each server's source, package page, and setup guide before
				installing anything.
			</P>
			<P>
				A directory solves discovery. Your review still decides whether a server
				is safe for your data and accounts. You end with candidates that have a
				publisher, source, install path, and clear purpose you can inspect.
			</P>
			<Screenshot
				src="/blog/mcp-server-security-checklist/allmcps-directory.jpg"
				alt="AllMCPs homepage with its MCP server search and browsing interface"
				caption="AllMCPs helps you discover MCP servers by task. Screenshot captured in August 2026."
			/>

			<H3>3. Verify the publisher and exact install command</H3>
			<P>
				Match the listing to the publisher's own repository or package page.
				Read the command in full, including its arguments and environment
				variables. Stop if the package name, repository, and publisher identity
				don't line up.
			</P>
			<P>
				This check matters most for local servers because their setup can run
				code on your machine. The MCP guidance tells clients to show the exact
				command and require consent before one-click configuration. You now know
				exactly what starts and where its code comes from.
			</P>

			<H3>4. Inspect every tool and resource the server exposes</H3>
			<P>
				Tool names aren't enough. Read their inputs and outputs, then separate
				read actions from write actions. Pay close attention to tools that send
				messages, change files, move money, publish content, or delete records.
			</P>
			<P>
				The{" "}
				<A href="https://modelcontextprotocol.io/docs/2026-07-28/learn/server-concepts">
					official server guide
				</A>{" "}
				explains that MCP tools can call APIs, modify files, and write to
				databases. At the end of this check, you can list the server's possible
				side effects in plain language.
			</P>

			<H3>5. Give it the smallest useful permission set</H3>
			<P>
				Use a separate account when the service supports one. Start with
				read-only scopes, a narrow folder, a test workspace, or a key made for
				this server. Use a dedicated credential instead of an admin key.
			</P>
			<P>
				For a remote server, check that authorization uses HTTPS and that the
				consent screen names the access it requests. Avoid setup guides that ask
				you to paste a broad access token into an unrelated service. A leaked or
				misused credential then has a small reach and a clear revocation path.
			</P>

			<H3>6. Test outside production and watch what happens</H3>
			<P>
				Use sample files, a test project, or an account with no customer data.
				Run the exact task you wrote down, then inspect the server's requests,
				tool calls, and output. Include a failure case so you can see how it
				behaves when data is missing or a service rejects an action.
			</P>
			<P>
				The{" "}
				<A href="https://modelcontextprotocol.io/docs/2026-07-28/tools/inspector">
					MCP Inspector
				</A>{" "}
				is the reference tool for testing and debugging MCP servers. A passing
				test shows that the server does only the work you asked for and that you
				can explain every side effect.
			</P>
			<Screenshot
				src="/blog/mcp-server-security-checklist/mcp-inspector.jpg"
				alt="Official MCP Inspector documentation showing its web, command line, and terminal clients"
				caption="The official MCP Inspector supports browser, command-line, and terminal testing. Screenshot captured in August 2026."
			/>

			<H3>7. Plan removal before granting live access</H3>
			<P>
				Write down how to disable the server, revoke its key, remove its local
				configuration, and find its activity log. If one of those steps is
				unclear, resolve it before connecting live data.
			</P>
			<P>
				A clear removal path lets you stop access without waiting for the
				publisher or your own support team. Recheck the server after updates
				that change its tools, permissions, package owner, or install command.
			</P>

			<H2>&gt; Match access to the cost of a wrong action</H2>
			<P>
				A calendar lookup and a customer refund shouldn't share the same
				approval policy. Ask what the worst plausible action is, how fast you
				would notice, and whether you can reverse it. The answer sets the right
				level of access and human review.
			</P>
			<P>
				This is the same trust problem covered in{" "}
				<A href="/blog/ai-agent-trust-gap">
					why AI agents stall between demo and revenue
				</A>
				. A narrow tool with an approval step is easier to trust than a broad
				tool with a vague promise of guardrails.
			</P>

			<H2>&gt; Frequently asked questions</H2>

			<H3>What is an MCP server?</H3>
			<P>
				An MCP server is a program that exposes tools, resources, or prompts to
				an AI application through the Model Context Protocol. The server may
				connect the AI to local files, a database, a web service, or another
				system.
			</P>

			<H3>Is an MCP directory listing safe to install?</H3>
			<P>
				A listing is a starting point, not a security review. Match it to the
				source repository and publisher, read the full install command, inspect
				permissions, and test with data you can replace.
			</P>

			<H3>Should I use a local or remote MCP server?</H3>
			<P>
				Choose based on the task and the data boundary. A local server runs code
				on your machine, so review its command and file access. A remote server
				adds a network service and an authorization flow you must trust.
			</P>

			<H3>How do I test an MCP server before using it?</H3>
			<P>
				Connect it to sample data and a limited account. List its tools, call
				the ones you need, inspect the output and logs, and try a failure case.
				Move to live data only after its behavior matches the task and
				permissions you defined.
			</P>

			<H2>&gt; Where AgentMRR fits</H2>
			<P>
				MCP security and business proof answer different trust questions. This
				checklist helps you judge what a server can access. AgentMRR shows which
				AI agent products have revenue verified through Stripe read-only access
				and ranks them on a public leaderboard.
			</P>
			<P>
				Use the <A href="/agent">AgentMRR CLI and API</A> when an agent needs
				structured leaderboard data. If you run an agent product, you can also{" "}
				<A href="/docs/submit">submit it for revenue verification</A>.
			</P>
			<P>
				<A href="/">Browse AI agents with verified revenue</A> and use
				commercial proof as one signal alongside your own security review.
			</P>
		</div>
	);
}
